import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Globe, ExternalLink, Check, AlertCircle, TestTube, Shield, Copy } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';

interface IntegrationSettings {
  onixx_site_url: string;
  onixx_api_key: string;
  onixx_company_id: string;
  portal_callback_url: string;
  modal_integration_enabled: string;
  onixx_whitelist_origins: string;
  portal_company_id: string;
}

const ApiPortalOnePage = () => {
  const { hasGlobalRole } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingOnixx, setTestingOnixx] = useState(false);
  const [settings, setSettings] = useState<IntegrationSettings>({
    onixx_site_url: '',
    onixx_api_key: '',
    onixx_company_id: '',
    portal_callback_url: '',
    modal_integration_enabled: 'true',
    onixx_whitelist_origins: '',
    portal_company_id: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', [
          'onixx_site_url', 'onixx_api_key', 'onixx_company_id', 'portal_callback_url',
          'modal_integration_enabled', 'onixx_whitelist_origins', 'portal_company_id'
        ]);

      if (error) throw error;

      const settingsMap = data.reduce((acc, item) => ({
        ...acc,
        [item.key]: item.value
      }), {} as IntegrationSettings);

      setSettings(settingsMap);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as configurações.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(settings).map(([key, value]) => ({
        key,
        value,
        updated_at: new Date().toISOString()
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('system_settings')
          .update({ value: update.value, updated_at: update.updated_at })
          .eq('key', update.key);

        if (error) throw error;
      }

      toast({
        title: "Sucesso",
        description: "Configurações salvas com sucesso.",
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar as configurações.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const testOnixxIntegration = async () => {
    setTestingOnixx(true);
    try {
      const { data, error } = await supabase.functions.invoke('portal-login', {
        body: {
          email: 'test@onixxengenharia.eng.br',
          password: 'test123',
          test_mode: true
        }
      });

      if (error) {
        toast({
          title: "Erro no Teste",
          description: `Falha na integração: ${error.message}`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "✅ Integração Funcionando",
          description: "A conexão com o portal-login está operacional!",
        });
      }
    } catch (error: any) {
      toast({
        title: "Erro no Teste",
        description: error.message || "Erro ao testar integração Onixx.",
        variant: "destructive",
      });
    } finally {
      setTestingOnixx(false);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copiado!",
        description: `${label} copiado para a área de transferência.`,
      });
    } catch (err) {
      toast({
        title: "Erro",
        description: "Não foi possível copiar para a área de transferência.",
        variant: "destructive",
      });
    }
  };

  if (!hasGlobalRole('portal_admin')) {
    return (
      <div className="text-center py-8">
        <h1 className="text-2xl font-bold mb-4">Acesso negado</h1>
        <p className="text-muted-foreground">
          Você não tem permissão para acessar as configurações de API.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const isOnixxConfigured = settings.onixx_site_url && settings.onixx_api_key && settings.onixx_company_id;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">APIs PortalONE</h1>
        <p className="text-muted-foreground">
          Configure as integrações com APIs externas e sistemas parceiros.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Integração Onixx Portal
          </CardTitle>
          <CardDescription>
            Configure a integração com o sistema Onixx para autenticação e sincronização de dados.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="onixxSiteUrl">URL do Site Onixx</Label>
            <Input
              id="onixxSiteUrl"
              type="url"
              placeholder="https://onixx.example.com"
              value={settings.onixx_site_url}
              onChange={(e) => setSettings({ ...settings, onixx_site_url: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="onixxApiKey">API Key</Label>
            <div className="flex gap-2">
              <Input
                id="onixxApiKey"
                type="password"
                placeholder="sk_live_..."
                value={settings.onixx_api_key}
                onChange={(e) => setSettings({ ...settings, onixx_api_key: e.target.value })}
              />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => copyToClipboard(settings.onixx_api_key, "API Key")}
                disabled={!settings.onixx_api_key}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="onixxCompanyId">Company ID</Label>
            <Input
              id="onixxCompanyId"
              placeholder="123456"
              value={settings.onixx_company_id}
              onChange={(e) => setSettings({ ...settings, onixx_company_id: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="portalCompanyId">Portal Company ID</Label>
            <Input
              id="portalCompanyId"
              placeholder="portal-company-123"
              value={settings.portal_company_id}
              onChange={(e) => setSettings({ ...settings, portal_company_id: e.target.value })}
            />
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="callbackUrl">URL de Callback</Label>
            <div className="flex gap-2">
              <Input
                id="callbackUrl"
                type="url"
                placeholder="https://www.portalone.adm.br/auth/portal-callback"
                value={settings.portal_callback_url}
                onChange={(e) => setSettings({ ...settings, portal_callback_url: e.target.value })}
              />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => copyToClipboard(settings.portal_callback_url, "URL de Callback")}
                disabled={!settings.portal_callback_url}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              URL para onde o usuário será redirecionado após autenticação no portal Onixx.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="whitelistOrigins">Origens Autorizadas (CORS)</Label>
            <Textarea
              id="whitelistOrigins"
              placeholder="https://app1.example.com,https://app2.example.com"
              value={settings.onixx_whitelist_origins}
              onChange={(e) => setSettings({ ...settings, onixx_whitelist_origins: e.target.value })}
              rows={3}
            />
            <p className="text-sm text-muted-foreground">
              Lista de origens separadas por vírgula que podem acessar a API.
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="modalIntegration"
              checked={settings.modal_integration_enabled === 'true'}
              onCheckedChange={(checked) => 
                setSettings({ ...settings, modal_integration_enabled: checked ? 'true' : 'false' })
              }
            />
            <Label htmlFor="modalIntegration">Habilitar Integração Modal</Label>
          </div>

          {isOnixxConfigured && (
            <Alert>
              <Check className="h-4 w-4" />
              <AlertDescription>
                Integração Onixx configurada com sucesso.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <Button 
              onClick={saveSettings} 
              disabled={saving}
              className="flex-1"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Salvar Configurações"
              )}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={testOnixxIntegration}
              disabled={testingOnixx || !isOnixxConfigured}
              className="flex-1"
            >
              {testingOnixx ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testando...
                </>
              ) : (
                <>
                  <TestTube className="mr-2 h-4 w-4" />
                  Testar Integração
                </>
              )}
            </Button>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Informações de Segurança
            </h3>
            <div className="grid gap-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Atenção:</strong> Mantenha a API Key segura e não a compartilhe publicamente. 
                  Use HTTPS sempre que possível.
                </AlertDescription>
              </Alert>
              
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  <strong>Webhook URL:</strong> Configure esta URL no sistema Onixx para receber notificações:
                </p>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value="https://emyfvhrhwuhlmxewvcyk.supabase.co/functions/v1/portal-login"
                    className="font-mono text-xs"
                  />
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => copyToClipboard("https://emyfvhrhwuhlmxewvcyk.supabase.co/functions/v1/portal-login", "Webhook URL")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ApiPortalOnePage;